import { useState, useRef, useEffect, useMemo } from 'react';
import PlusIcon from '@/assets/Icons/components/data-table/PlusIcon';
import RightIcon from '@/assets/Icons/components/RightIcon';
import LeftIcon from '@/assets/Icons/components/LeftIcon';
import Calender from '@/assets/Icons/components/date-picker/Calender';
import ChevronUp from '@/assets/Icons/components/date-picker/ChevronUp';
import CustomInformationField from './CustomInformationInput';
import TrashIcon from '@/assets/Icons/components/TrashIcon';

import { useDebounce } from '@/hooks/useDebounce';
import { setHasUnsavedChanges } from '@/reducers/formSlice';
import { useDispatch } from 'react-redux';

import Modal from '../Modal/Modal';
import DeleteRowPopup from './DeleteRowPopup';
import Tooltip from '@/components/Tooltip/Tooltip';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';

// Type definitions remain the same
interface Column {
  column_id: string;
  order: number;
  label: string;
  data_type?: string;
  is_required?: boolean;
  options?: any[];
}

interface TableValue {
  column_id: string;
  column_order: number;
  row_order: number;
  row_id?: string;
  value: string;
}

interface TableConfig {
  dimensions?: {
    rows: number;
    columns: number;
  };
  headers?: {
    columnHeader: boolean;
    rowHeader: boolean;
  };
  additionalRows?: boolean;
  columnGroupingEnabled?: boolean;
}

interface ColumnGroup {
  group_id: string;
  name: string;
  order: number;
}

interface TableData {
  table_id: string;
  label?: string;
  columnHeaders: Column[];
  values: TableValue[];
  columnGroups?: ColumnGroup[];
  additional_config?: TableConfig;
  rowHeaders?: Row[];
}

interface Row {
  row_id: string;
  order: number;
  label: string;
}

interface TabResponse {
  tab_id: string;
  name: string;
  table_responses: TableData[];
  field_responses?: any[];
  is_admin_review?: boolean;
}

interface FormData {
  label?: string;
  tab_responses: TabResponse[];
  tabs_count?: number;
}

interface TableDataByTab {
  [tabId: string]: TableData[];
}

interface CustomTabsWithTableProps {
  formData: FormData;
  onDataChange?: (updatedData: FormData) => void;
  isLockedByMe?: boolean;
  lockedOwner?: string;
  isReviewForm?: boolean;
  collaborationPopup?: any;
  isCancel?: boolean;
  setIsCancel?: any;
  shouldHighlightTab?: (tab: any) => boolean;
  shouldHighlightTable?: (table: any) => boolean;
  shouldHighlight?: boolean;
}

// Enhanced MonthYearPicker component with manual input capability
type MonthYearPickerProps = {
  name?: string;
  value?: string;
  onChange: (event: { target: { name: string; value: string; type: string } }) => void;
  placeholder?: string;
  minDate?: string; // Add this line
  isReviewForm?: boolean; // Add this line to handle review form state
};

const MonthYearPicker = ({
  name = 'date',
  value = '',
  onChange,
  placeholder = 'MM/YYYY',
  minDate, // Add this line
  isReviewForm = false, // Add this line to handle review form state
}: MonthYearPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [displayValue, setDisplayValue] = useState(value || '');
  const [openDirection, setOpenDirection] = useState('down');
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  // Get current date to check for future dates
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentSystemYear = today.getFullYear();

  // Parse initial value if provided
  useEffect(() => {
    if (value) {
      const date = parseDate(value);
      if (date) {
        setSelectedDate(date);
        setCurrentYear(date.getFullYear());
        setDisplayValue(formatDate(date));
        setInputValue(formatDate(date));
      } else {
        setDisplayValue('');
        setInputValue('');
      }
    } else {
      setDisplayValue('');
      setInputValue('');
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowYearSelector(false);

        // If user was editing, validate and apply the input value when clicking outside
        if (isEditing) {
          validateAndApplyInput();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, inputValue]);

  // Parse date from string
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;

    // Handle MM/YYYY format
    const parts: string[] = dateStr.split('/');
    if (parts.length === 2) {
      const month: number = parseInt(parts[0], 10) - 1;
      const year: number = parseInt(parts[1], 10);

      if (
        !isNaN(month) &&
        !isNaN(year) &&
        month >= 0 &&
        month <= 11 &&
        year > 1900 &&
        year < 2100
      ) {
        return new Date(year, month, 1);
      }
    }

    return null;
  };

  // Format date to string
  const formatDate = (date: Date | null): string => {
    if (!date) return '';

    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: number = date.getFullYear();
    return `${month}/${year}`;
  };

  // Check if a date is in the future
  const isFutureDate = (year: number, month: number): boolean => {
    if (year > currentSystemYear) return true;
    if (year === currentSystemYear && month > currentMonth) return true;
    return false;
  };

  // Toggle the date picker
  const togglePicker = () => {
    if (!isEditing) {
      if (!isOpen && inputRef.current) {
        // Calculate position before opening
        const inputRect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;
        const pickerHeight = 300; // Approximate height of the picker

        if (spaceBelow < pickerHeight && spaceAbove > pickerHeight) {
          setOpenDirection('up');
        } else {
          setOpenDirection('down');
        }
      }
      setIsOpen(!isOpen);
      setShowYearSelector(false);
    }
  };

  // Get the month name
  const getMonthName = (month: number): string => {
    const monthNames: string[] = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return monthNames[month];
  };

  // Select a month
  const selectMonth = (month: number): void => {
    // Don't allow selection of future months
    if (isFutureDate(currentYear, month)) {
      return;
    }

    const newDate: Date = new Date(currentYear, month, 1);
    const formattedDate: string = formatDate(newDate);

    setDisplayValue(formattedDate);
    setInputValue(formattedDate);
    setSelectedDate(newDate);
    setIsOpen(false);
    setShowYearSelector(false);

    // Trigger onChange with the formatted date
    const event: { target: { name: string; value: string; type: string } } = {
      target: {
        name,
        value: formattedDate,
        type: 'date',
      },
    };
    onChange(event);
  };

  // Select a year
  const selectYear = (year: number): void => {
    setCurrentYear(year);
    setShowYearSelector(false);
  };

  // Check if a month is selected
  const isSelected = (month: number): boolean => {
    if (!selectedDate) return false;
    return month === selectedDate.getMonth() && currentYear === selectedDate.getFullYear();
  };

  // Check if a year is selected
  const isYearSelected = (year: number): boolean => {
    return year === currentYear;
  };

  // Get an array of years for the year selector
  const getYearRange = () => {
    const startYear = 2017;
    const endYear = 2035; // Keep the original end year
    const years = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }

    return years;
  };

  // Toggle between month selector and year selector
  const toggleYearSelector = () => {
    setShowYearSelector(!showYearSelector);
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>): void => {
    e.stopPropagation();

    trackEvent(PostHogEvents.FORM_FIELD_FOCUSED, {
      timestamp: new Date().toISOString(),
    });
  };

  // Handle input change during manual editing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  // Validate the manually entered date
  const validateAndApplyInput = () => {
    // Try to parse the input
    const date = parseDate(inputValue);

    if (date) {
      // Check if date is in the future
      if (isFutureDate(date.getFullYear(), date.getMonth())) {
        // Invalid future date, revert to previous value
        setInputValue(displayValue);
        setIsEditing(false);
        return;
      }

      // Valid date format
      const formattedDate = formatDate(date);
      setDisplayValue(formattedDate);
      setSelectedDate(date);
      setCurrentYear(date.getFullYear());

      // Trigger onChange with the validated date
      onChange({
        target: {
          name,
          value: formattedDate,
          type: 'date',
        },
      });
    } else {
      // Invalid format, revert to previous value
      setInputValue(displayValue);
    }

    setIsEditing(false);
  };

  // Handle key press events for input validation
  interface HandleKeyDownEvent {
    key: string;
    preventDefault: () => void;
  }

  const handleKeyDown = (e: HandleKeyDownEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAndApplyInput();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setInputValue(displayValue);
      setIsEditing(false);
    } else if (e.key === 'Backspace' && inputValue === '') {
      // Clear the date when backspace is pressed on empty field
      setSelectedDate(null);
      setDisplayValue('');
      setInputValue('');

      // Trigger onChange with empty value
      onChange({
        target: {
          name,
          value: '',
          type: 'date',
        },
      });
    }
  };

  // Add this function to compare with minDate
  const isBeforeMinDate = (year: number, month: number): boolean => {
    if (!minDate) return false;
    const [minMonth, minYear] = minDate.split('/').map(Number);
    if (year < minYear) return true;
    if (year === minYear && month < minMonth - 1) return true;
    return false;
  };

  return (
    <div className="relative" ref={pickerRef}>
      {/* Input field with calendar icon */}
      <div className="flex items-center border-gray-300 rounded">
        <div
          className="w-full flex items-center cursor-pointer"
          onClick={isEditing ? undefined : togglePicker}
        >
          <input
            ref={inputRef}
            type="text"
            className="outline-none bg-transparent text-gray-700 text-xs w-full px-2 text-right"
            placeholder={placeholder}
            value={isEditing ? inputValue : displayValue}
            onClick={handleInputClick}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={isEditing ? validateAndApplyInput : undefined}
            readOnly={!isEditing || isReviewForm}
          />
          {!isReviewForm && (
            <div className="cursor-pointer" onClick={togglePicker}>
              <Calender />
            </div>
          )}
        </div>
      </div>

      {/* Dropdown selector */}
      {isOpen && !isEditing && (
        <div
          className={`absolute z-10 ${
            openDirection === 'down' ? 'mt-3' : 'mb-1 bottom-full'
          } right-0 w-64 bg-white rounded shadow-lg overflow-hidden`}
        >
          {/* Header with month/year toggle */}
          <div
            className="flex justify-center items-center p-3 cursor-pointer border-b-2 border-gray-100"
            onClick={toggleYearSelector}
          >
            <div
              className={`w-full flex justify-between items-center text-gray-800 font-medium gap-2 p-2 rounded ${
                showYearSelector ? 'bg-blue-50 text-blue-600' : 'border-2 border-gray-100'
              }`}
            >
              {showYearSelector
                ? `${currentYear}`
                : `${getMonthName(selectedDate?.getMonth() || new Date().getMonth())} ${currentYear}`}
              <span>
                <ChevronUp />
              </span>
            </div>
          </div>

          {showYearSelector ? (
            /* Year selector grid */
            <div className="px-2 py-1 max-h-36 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2">
                {getYearRange().map((year) => (
                  <div
                    key={year}
                    className={`
                      text-center py-1 px-1 rounded text-sm
                      ${
                        isYearSelected(year)
                          ? 'bg-blue-100 text-white'
                          : year > currentSystemYear
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
                      }
                    `}
                    onClick={() => (year <= currentSystemYear ? selectYear(year) : null)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Month selector grid */
            <div className="p-2">
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className={`
                      text-center py-1 px-1 rounded cursor-pointer text-sm
                      ${
                        isSelected(i)
                          ? 'bg-blue-100 text-white'
                          : isFutureDate(currentYear, i) || isBeforeMinDate(currentYear, i)
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-800 hover:bg-gray-100'
                      }
                    `}
                    onClick={() =>
                      !isFutureDate(currentYear, i) && !isBeforeMinDate(currentYear, i)
                        ? selectMonth(i)
                        : null
                    }
                  >
                    {getMonthName(i)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomTabsWithTable = ({
  formData,
  onDataChange,
  isLockedByMe,
  lockedOwner,
  isReviewForm,
  isCancel = false,
  collaborationPopup,
  setIsCancel,
  shouldHighlightTab,
  shouldHighlightTable,
  // shouldHighlight,
}: CustomTabsWithTableProps) => {
  // 1. Declare localTableData state FIRST
  const [localTableData, setLocalTableData] = useState<TableDataByTab>(() => {
    const initialData: TableDataByTab = {};
    formData?.tab_responses.forEach((tab) => {
      initialData[tab.tab_id] = JSON.parse(JSON.stringify(tab.table_responses || []));
    });
    return initialData;
  });

  const question = formData?.tabs_count === 7;
  const [activeTab, setActiveTab] = useState<string>(formData?.tab_responses[0]?.tab_id);
  const [scrollPosition, setScrollPosition] = useState(0);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);

  const dispatch = useDispatch();

  // State to track original and current table data
  const [originalTableData, setOriginalTableData] = useState<TableDataByTab>({});

  // Store original data when component mounts
  useEffect(() => {
    const initialData: TableDataByTab = {};
    formData?.tab_responses.forEach((tab) => {
      initialData[tab.tab_id] = JSON.parse(JSON.stringify(tab.table_responses || []));
    });
    setOriginalTableData(initialData);
  }, []);

  // Handle cancel functionality
  useEffect(() => {
    if (isCancel) {
      // Restore original data
      setLocalTableData(JSON.parse(JSON.stringify(originalTableData)));

      // Reset delete popup state
      setIsDeletePopupOpen(false);
      setRowToDelete(null);

      // Update parent form data
      if (onDataChange && formData) {
        const updatedFormData = {
          ...formData,
          tab_responses: formData.tab_responses.map((tab) => ({
            ...tab,
            table_responses: originalTableData[tab.tab_id] || [],
          })),
        };
        onDataChange(updatedFormData);
      }

      // Reset cancel flag if setter is provided
      if (setIsCancel) {
        setIsCancel(false);
      }
    }
  }, [isCancel]);

  // Check if all required date ranges are present for question scenario
  const checkDateRangesComplete = useMemo(() => {
    if (!question || !formData?.tab_responses) return {};

    const requiredMonths = new Set<string>();

    // Fill requiredMonths from September 2017 to March 2025
    const start = new Date(2017, 8); // September = 8 (0-based)
    const end = new Date(2025, 2); // March = 2

    const current = new Date(start);
    while (current <= end) {
      const monthStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
      requiredMonths.add(monthStr);
      current.setMonth(current.getMonth() + 1);
    }

    const tabStatus: Record<string, boolean> = {};

    const extractMonthYear = (dateStr?: string): { year: number; month: number } | null => {
      if (!dateStr) return null;
      let parts: string[];
      if (dateStr.includes('/')) {
        parts = dateStr.split('/');
        if (parts.length === 2)
          return { month: parseInt(parts[0], 10), year: parseInt(parts[1], 10) };
      } else if (dateStr.includes('-')) {
        parts = dateStr.split('-');
        if (parts.length === 2)
          return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) };
      }
      return null;
    };

    const getMonthRange = (
      start: { year: number; month: number },
      end: { year: number; month: number }
    ): string[] => {
      const result: string[] = [];
      const current = new Date(start.year, start.month - 1);
      const endDate = new Date(end.year, end.month - 1);
      while (current <= endDate) {
        result.push(`${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`);
        current.setMonth(current.getMonth() + 1);
      }
      return result;
    };

    for (const tab of formData.tab_responses) {
      const tabId = tab.tab_id;
      const tables = localTableData[tabId] || [];
      const coveredMonths = new Set<string>();

      for (const table of tables) {
        const beginDateCol = table.columnHeaders.find((col) =>
          col.label.toLowerCase().includes('beginning date')
        );
        const endDateCol = table.columnHeaders.find((col) =>
          col.label.toLowerCase().includes('end date')
        );
        if (!beginDateCol || !endDateCol) continue;

        const beginValues = table.values.filter((v) => v.column_id === beginDateCol.column_id);
        const endValues = table.values.filter((v) => v.column_id === endDateCol.column_id);

        const beginMap = new Map(beginValues.map((v) => [v.row_order, v.value]));
        const endMap = new Map(endValues.map((v) => [v.row_order, v.value]));

        for (const rowOrder of beginMap.keys()) {
          const begin = extractMonthYear(beginMap.get(rowOrder));
          const end = extractMonthYear(endMap.get(rowOrder));
          if (begin && end) {
            const rangeMonths = getMonthRange(begin, end);
            for (const m of rangeMonths) {
              coveredMonths.add(m);
            }
          }
        }
      }

      const isComplete = [...requiredMonths].every((month) => coveredMonths.has(month));
      tabStatus[tabId] = isComplete;
    }

    return tabStatus;
  }, [localTableData, question, formData]);

  // Update tableDataByTab if formData changes externally
  useEffect(() => {
    if (formData) {
      const updatedData: TableDataByTab = {};
      formData.tab_responses.forEach((tab) => {
        updatedData[tab.tab_id] = JSON.parse(JSON.stringify(tab.table_responses || []));
      });
      setLocalTableData(updatedData);
    }
  }, [formData]);

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Function to scroll to the selected tab
  const scrollToTab = (tabId: string) => {
    const tabElement = tabRefs.current[tabId];
    if (tabElement && tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const tabRect = tabElement.getBoundingClientRect();

      // Calculate scroll position to center the tab
      const scrollLeft =
        tabRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 4 +
        tabRect.width / 4;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Save data from current tab before changing tabs
  const handleTabChange = (tabId: string): void => {
    // First, make sure current tab data is updated in parent component
    updateParentData();

    // Then change the active tab and scroll to it
    setActiveTab(tabId);
    setTimeout(() => {
      scrollToTab(tabId);
    }, 200);

    // Find the tab name from formData
    const tab = formData?.tab_responses?.find((t) => t.tab_id === tabId);
    const tabName = tab?.name || tabId;

    // You need to have formId available in this component
    trackEvent(PostHogEvents.TAB_CHANGED, {
      tab_name: tabName,
      form_id: tabId || '',
      message: [PostHogMessages[PostHogEvents.TAB_CHANGED]],
    });
  };

  // Function to update parent component data with all tab data
  const updateParentData = () => {
    if (!onDataChange) return;

    const updatedFormData = { ...formData };

    // Update ALL tabs in the form data with current state
    updatedFormData.tab_responses = updatedFormData.tab_responses.map((tab) => {
      return {
        ...tab,
        table_responses: localTableData[tab.tab_id] || tab.table_responses || [],
      };
    });

    onDataChange(updatedFormData);
  };

  // Handle adding a new row
  const handleAddRow = (tableIndex: number, afterRowIndex: number): void => {
    const currentTabData = [...(localTableData[activeTab] || [])];
    const tableToUpdate = { ...currentTabData[tableIndex] };

    // Get columns and values from the table structure
    const columnHeaders = tableToUpdate.columnHeaders || [];
    const allValues = tableToUpdate.values || [];

    // Get all unique row orders and sort them
    const allRowOrders = Array.from(new Set(allValues.map((v) => v.row_order))).sort(
      (a, b) => a - b
    );

    // Build valueMap for easier lookup
    const valueMap: Record<number, Record<string, any>> = {};
    allValues.forEach((value) => {
      if (!valueMap[value.row_order]) {
        valueMap[value.row_order] = {};
      }
      valueMap[value.row_order][value.column_id] = value.value;
    });

    const emptyCells: { rowIndex: number; columnIndex: number }[] = [];

    // Check each existing row for empty cells
    allRowOrders.forEach((rowOrder, rowIdx) => {
      columnHeaders.forEach((column, colIndex) => {
        const cellValue = valueMap[rowOrder]?.[column.column_id];
        if (
          !cellValue ||
          cellValue === undefined ||
          cellValue === null ||
          cellValue.toString().trim() === ''
        ) {
          emptyCells.push({ rowIndex: rowIdx, columnIndex: colIndex });
        }
      });
    });

    // If there are empty cells, highlight them and return
    if (emptyCells.length > 0) {
      // Add visual feedback to empty cells
      emptyCells.forEach(({ rowIndex, columnIndex }) => {
        const cellElement = document.querySelector(
          `[data-table-index="${tableIndex}"][data-row-index="${rowIndex}"][data-column-index="${columnIndex}"] input`
        );
        if (cellElement) {
          cellElement.classList.add('border-red-500');
          setTimeout(() => {
            cellElement.classList.remove('border-red-500');
          }, 3000);
        }
      });

      return;
    }

    // If validation passes, proceed with adding new row
    setLocalTableData((prevState) => {
      const newState = { ...prevState };
      const tabData = [...(newState[activeTab] || [])];
      const tableToUpdate = { ...tabData[tableIndex] };

      // Get current row orders
      const currentRowOrders = Array.from(
        new Set(tableToUpdate.values.map((v) => v.row_order))
      ).sort((a, b) => a - b);

      // Calculate the new row order
      let newRowOrder: number;
      if (currentRowOrders.length === 0) {
        newRowOrder = 1;
      } else if (afterRowIndex >= currentRowOrders.length - 1) {
        // Adding at the end
        newRowOrder = currentRowOrders[currentRowOrders.length - 1] + 1;
      } else {
        // Adding in between
        const afterRowOrder = currentRowOrders[afterRowIndex];
        const nextRowOrder = currentRowOrders[afterRowIndex + 1];
        newRowOrder = (afterRowOrder + nextRowOrder) / 2;
      }

      // Create empty values for the new row
      const newRowValues = columnHeaders.map((header) => ({
        column_id: header.column_id,
        column_order: header.order,
        row_order: newRowOrder,
        value: '',
      }));

      // Add the new row values
      tableToUpdate.values = [...tableToUpdate.values, ...newRowValues];

      // Sort values by row_order and column_order for consistency
      tableToUpdate.values.sort((a, b) => {
        if (a.row_order !== b.row_order) return a.row_order - b.row_order;
        return a.column_order - b.column_order;
      });

      // Update row count
      if (tableToUpdate.additional_config?.dimensions?.rows !== undefined) {
        tableToUpdate.additional_config = {
          ...tableToUpdate.additional_config,
          dimensions: {
            ...tableToUpdate.additional_config.dimensions,
            rows: tableToUpdate.additional_config.dimensions.rows + 1,
          },
        };
      }

      // Update table in tab
      tabData[tableIndex] = tableToUpdate;
      newState[activeTab] = tabData;

      return newState;
    });
  };

  // Store initial data in a ref for reset functionality
  const initialDataRef = useRef<TableDataByTab>(localTableData);

  // Update initial data ref when formData changes
  useEffect(() => {
    const newInitialData: TableDataByTab = {};
    formData?.tab_responses.forEach((tab) => {
      newInitialData[tab.tab_id] = JSON.parse(JSON.stringify(tab.table_responses || []));
    });
    initialDataRef.current = newInitialData;
  }, [formData]);

  // Handle isCancel changes
  useEffect(() => {
    if (isCancel) {
      setLocalTableData(JSON.parse(JSON.stringify(originalTableData)));
    }
  }, [isCancel]);

  // Debounce the local changes before sending to parent
  const debouncedTableData = useDebounce(localTableData, 300);

  // Update parent when debounced value changes
  useEffect(() => {
    if (onDataChange) {
      const updatedFormData = { ...formData };
      updatedFormData.tab_responses = updatedFormData.tab_responses.map((tab) => ({
        ...tab,
        table_responses: debouncedTableData[tab.tab_id] || tab.table_responses || [],
      }));
      onDataChange(updatedFormData);
    }
  }, [debouncedTableData]);

  // Update local state immediately when formData changes externally
  useEffect(() => {
    if (formData) {
      const updatedData: TableDataByTab = {};
      formData.tab_responses.forEach((tab) => {
        updatedData[tab.tab_id] = JSON.parse(JSON.stringify(tab.table_responses || []));
      });
      setLocalTableData(updatedData);
    }
  }, [formData]);

  // Handle cell value changes
  const handleCellChange = (
    tableIndex: number,
    rowIndex: number,
    columnId: string,
    value: string
  ) => {
    setLocalTableData((prevState) => {
      const newState = { ...prevState };
      const tabData = [...(newState[activeTab] || [])];
      const tableToUpdate = { ...tabData[tableIndex] };

      // Get all unique row orders and sort them
      const allRowOrders = Array.from(new Set(tableToUpdate.values.map((v) => v.row_order))).sort(
        (a, b) => a - b
      );

      // Get the actual row_order for this display index
      const actualRowOrder = allRowOrders[rowIndex] || rowIndex + 1;

      // Find if this is an "End Date" column
      const column = tableToUpdate.columnHeaders.find((h) => h.column_id === columnId);
      if (column && column.label.toLowerCase().includes('end date')) {
        // Find the "Begin Date" column in the same row
        const beginCol = tableToUpdate.columnHeaders.find((h) =>
          h.label.toLowerCase().includes('beginning date')
        );
        if (beginCol) {
          const beginValue = tableToUpdate.values.find(
            (v) => v.row_order === actualRowOrder && v.column_id === beginCol.column_id
          )?.value;
          if (beginValue && isDateLessThan(value, beginValue)) {
            return prevState;
          }
        }
      }

      const valueIndex = tableToUpdate.values.findIndex(
        (v) => v.row_order === actualRowOrder && v.column_id === columnId
      );

      dispatch(setHasUnsavedChanges(true));

      const newValues = [...tableToUpdate.values];
      if (valueIndex >= 0) {
        newValues[valueIndex] = {
          ...newValues[valueIndex],
          value: value,
        };
      } else {
        if (column) {
          newValues.push({
            column_id: columnId,
            column_order: column.order,
            row_order: actualRowOrder,
            value: value,
          });
        }
      }

      tableToUpdate.values = newValues;
      tabData[tableIndex] = tableToUpdate;
      newState[activeTab] = tabData;

      return { ...newState };
    });
  };
  // Function to render our custom date picker
  const renderDatePicker = (
    tableIndex: number,
    rowIndex: number,
    columnId: string,
    value: string
  ) => {
    // Find the table and columns
    const table = localTableData[activeTab]?.[tableIndex];
    const column = table?.columnHeaders.find((h) => h.column_id === columnId);

    // If this is an "End Date" column, find the "Begin Date" value for this row
    let minDate: string | undefined = undefined;
    if (column && column.label.toLowerCase().includes('end date')) {
      const beginCol = table?.columnHeaders.find((h) =>
        h.label.toLowerCase().includes('beginning date')
      );
      if (beginCol) {
        minDate =
          table.values.find(
            (v) => v.row_order === rowIndex + 1 && v.column_id === beginCol.column_id
          )?.value || undefined;
      }
    }

    const handleDateChange = (e: { target: { name: string; value: string; type: string } }) => {
      handleCellChange(tableIndex, rowIndex, columnId, e.target.value);
    };

    return (
      <div>
        <MonthYearPicker
          name={`${columnId}-${rowIndex}`}
          value={value}
          onChange={handleDateChange}
          placeholder="MM/YYYY"
          minDate={minDate} // Pass minDate here
          isReviewForm={isReviewForm}
        />
      </div>
    );
  };

  // Updated renderTable function - show delete button for all rows except the first one
  const renderTable = (tableData: TableData, tableIndex: number) => {
    const headers = tableData.columnHeaders;

    const isTableHighlighted = shouldHighlightTable ? shouldHighlightTable(tableData) : false;

    // Process values into a row-based structure
    const rows: Record<number, Record<string, string>> = tableData.values.reduce(
      (acc, curr) => {
        if (!acc[curr.row_order]) acc[curr.row_order] = {};
        acc[curr.row_order][curr.column_id] = curr.value;
        return acc;
      },
      {} as Record<number, Record<string, string>>
    );

    // Get all unique row orders and sort them
    const allRowOrders = Array.from(new Set(tableData.values.map((v) => v.row_order))).sort(
      (a, b) => a - b
    );

    // Ensure we have at least one row to display
    const displayRowOrders = allRowOrders.length > 0 ? allRowOrders : [1];

    // Determine the number of rows to display
    const numRows = Math.max(
      displayRowOrders.length,
      tableData.additional_config?.dimensions?.rows || 1
    );

    // UPDATED: Function to determine if delete button should be shown
    const shouldShowDeleteButton = (rowIndex: number): boolean => {
      return rowIndex >= 0;
    };

    // Add delete row handler
    const handleDeleteClick = (rowIndex: number) => {
      // const rowOrderToDelete = displayRowOrders[rowIndex] || rowIndex + 1;
      setRowToDelete(rowIndex);
      setIsDeletePopupOpen(true);
    };

    const handleDeleteRow = (tableIndex: number, rowIndex: number) => {
      setLocalTableData((prevState) => {
        const newState = { ...prevState };
        const tabData = [...(newState[activeTab] || [])];
        const tableToUpdate = { ...tabData[tableIndex] };

        // Get all unique row orders and sort them
        const allRowOrders = Array.from(
          new Set(tableToUpdate.values.map((value) => value.row_order))
        ).sort((a, b) => a - b);

        // Get the actual row_order for the row to delete (not the display index)
        const rowOrderToDelete = allRowOrders[rowIndex];

        // Prevent deletion if only one row exists
        if (allRowOrders.length <= 1) {
          return prevState; // Don't delete the last remaining row
        }

        // Filter out values for the deleted row
        tableToUpdate.values = tableToUpdate.values.filter(
          (value) => value.row_order !== rowOrderToDelete
        );

        // Update dimensions if they exist
        if (tableToUpdate.additional_config?.dimensions?.rows !== undefined) {
          tableToUpdate.additional_config = {
            ...tableToUpdate.additional_config,
            dimensions: {
              ...tableToUpdate.additional_config.dimensions,
              rows: Math.max(1, tableToUpdate.additional_config.dimensions.rows - 1),
            },
          };
        }

        tabData[tableIndex] = tableToUpdate;
        newState[activeTab] = tabData;

        return newState;
      });
    };

    const confirmDelete = () => {
      if (rowToDelete !== null) {
        handleDeleteRow(tableIndex, rowToDelete);
      }
      setIsDeletePopupOpen(false);
      setRowToDelete(null);
    };

    const cancelDelete = () => {
      setIsDeletePopupOpen(false);
      setRowToDelete(null);
    };

    return (
      <div
        className={`${isTableHighlighted ? 'p-2 ring-2 ring-[#EF8B21] bg-[#FCF2E6] rounded-lg' : ''}`}
      >
        {isTableHighlighted && (
          <div className="mb-2 flex items-center text-sm text-[#EF8B21] font-medium">
            <div className="w-2 h-2 bg-[#EF8B21] rounded-full mr-2"></div>
            This table requires your attention for review
          </div>
        )}

        <table className="w-full mt-4 border-collapse">
          <thead>
            <tr className="bg-blue-50 text-ds-gray-600 font-semibold text-sm">
              {/* Column for add row buttons */}
              {!isReviewForm && <th className="px-2"></th>}

              {/* Column headers */}
              {headers.map((header) => (
                <th
                  key={header.column_id}
                  className={`text-sm border-r border-l px-4 py-2 capitalize text-ds-gray-600 font-semibold text-left`}
                >
                  {header.label}
                </th>
              ))}
              {/* Action column header */}
              {!isReviewForm && (
                <th className="text-sm border-r border-l px-2 py-2 capitalize text-ds-gray-600 font-semibold text-left">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {[...Array(numRows)].map((_, rowIndex) => {
              // Get the actual row_order for this display index
              const rowOrder = displayRowOrders[rowIndex] || rowIndex + 1;
              const showDeleteButton = shouldShowDeleteButton(rowIndex);

              return (
                <tr key={rowIndex} className="bg-white text-center m-auto">
                  {/* Add row button cell */}
                  {!isReviewForm && (
                    <td className="border border-ds-gray-100 text-center w-14 m-auto">
                      {tableData.additional_config?.additionalRows !== false && (
                        <Tooltip
                          content={'Please fill all fields before adding a new row'}
                          position="bottom"
                          width="150px"
                        >
                          <button
                            onClick={() => handleAddRow(tableIndex, rowIndex)}
                            className={`w-14 h-6 flex items-center justify-center rounded-full focus:outline-none`}
                            aria-label="Add row"
                          >
                            <span className="flex justify-center items-center">
                              <PlusIcon />
                            </span>
                          </button>
                        </Tooltip>
                      )}
                    </td>
                  )}

                  {/* Data cells */}
                  {headers.map((header) => {
                    const cellValue = rows[rowOrder]?.[header.column_id] || '';
                    const isDateField = header.data_type === 'date';

                    return (
                      <td
                        key={header.column_id}
                        className="border px-1 py-2 text-xs text-ds-gray-500"
                        style={{ width: `${100 / headers.length}%` }}
                        data-table-index={tableIndex}
                        data-row-index={rowIndex}
                        data-column-index={headers.indexOf(header)}
                      >
                        {isDateField ? (
                          renderDatePicker(tableIndex, rowIndex, header.column_id, cellValue)
                        ) : (
                          <input
                            type="text"
                            className="w-full outline-none placeholder:text-ds-gray-300"
                            placeholder={header.label}
                            value={cellValue}
                            onChange={(e) =>
                              handleCellChange(
                                tableIndex,
                                rowIndex,
                                header.column_id,
                                e.target.value
                              )
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (lockedOwner !== '' && !isLockedByMe) {
                                if (collaborationPopup) {
                                  collaborationPopup(true);
                                }
                              }
                            }}
                            readOnly={(lockedOwner !== '' && !isLockedByMe) || isReviewForm}
                            disabled={false}
                          />
                        )}
                      </td>
                    );
                  })}

                  {/* Delete row button cell - UPDATED LOGIC */}
                  {!isReviewForm && (
                    <td className="border px-1 py-2 text-xs text-ds-gray-500">
                      {showDeleteButton ? (
                        <button
                          onClick={() => handleDeleteClick(rowIndex)}
                          className="p-1 text-red-500 hover:text-red-700 focus:outline-none"
                          disabled={lockedOwner !== '' && !isLockedByMe}
                          aria-label="Delete row"
                        >
                          <TrashIcon />
                        </button>
                      ) : (
                        <div className="p-1 w-6 h-6"></div> // Empty space for alignment
                      )}
                    </td>
                  )}
                </tr>
              );
            })}

            {isDeletePopupOpen && (
              <Modal
                isOpen={isDeletePopupOpen}
                onClose={cancelDelete}
                title="Delete Row"
                size="sm"
                modalName="delete_row_popup_model"
              >
                <DeleteRowPopup onConfirm={confirmDelete} onCancel={cancelDelete} />
              </Modal>
            )}
          </tbody>
        </table>
      </div>
    );
  }; // Get the data for the active tab
  const activeTabData = formData?.tab_responses.find((tab) => tab.tab_id === activeTab);
  const activeTabTables = useMemo(() => {
    return localTableData[activeTab] || activeTabData?.table_responses || [];
  }, [localTableData, activeTab, activeTabData]);

  const scrollLeft = () => {
    const container = tabsContainerRef.current;
    if (container) {
      const newScrollPosition = Math.max(0, scrollPosition - 300);
      container.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
      setScrollPosition(newScrollPosition);
    }
  };

  // Scroll tabs right
  const scrollRight = () => {
    const container = tabsContainerRef.current;
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newScrollPosition = Math.min(maxScroll, scrollPosition + 300);
      container.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
      setScrollPosition(newScrollPosition);
    }
  };

  // Update scroll position when container scrolls
  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  // Save data when unmounting component
  useEffect(() => {
    return () => {
      updateParentData();
    };
  }, []);

  return (
    <div className={`w-full `}>
      <div className="mb-6 mt-6 font-semibold text-ds-gray-800">{formData?.label}</div>
      <div className="w-full border-b mb-4 border-ds-gray-100"></div>

      {/* Tabs */}
      <div className="mt-2 w-full">
        <div className="flex items-center">
          <button className="p-2 text-gray-500 focus:outline-none" onClick={scrollLeft}>
            <LeftIcon />
          </button>
          <div ref={tabsContainerRef} className="flex-grow overflow-hidden" onScroll={handleScroll}>
            <div className="flex w-10">
              {formData?.tab_responses.map((tab) => {
                // Check if this tab should be highlighted
                const isTabHighlighted = shouldHighlightTab ? shouldHighlightTab(tab) : false;

                return (
                  <button
                    key={tab.tab_id}
                    ref={(el) => {
                      tabRefs.current[tab.tab_id] = el;
                    }}
                    onClick={() => handleTabChange(tab.tab_id)}
                    className={`relative flex flex-col items-center py-2 mx-2 px-4 text-sm font-medium whitespace-nowrap ${
                      isTabHighlighted ? 'bg-[#FCF2E6] border-2 border-[#EF8B21] rounded-t-lg' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <span
                        className={`text-nowrap ${
                          activeTab === tab.tab_id
                            ? isTabHighlighted
                              ? 'text-[#EF8B21]'
                              : 'text-ds-gray-600'
                            : isTabHighlighted
                              ? 'text-[#EF8B21]'
                              : 'text-ds-gray-500'
                        }`}
                      >
                        {tab.name}
                      </span>
                      {/* Show highlight indicator */}
                      {isTabHighlighted && (
                        <span className="inline-flex h-2 w-2 rounded-full bg-[#EF8B21] ml-1" />
                      )}
                    </div>

                    <div
                      className={`absolute bottom-0 left-0 right-0 h-1 transition-all duration-300 rounded ${
                        activeTab === tab.tab_id
                          ? isTabHighlighted
                            ? 'bg-[#EF8B21]'
                            : 'bg-ds-mc-blue-300'
                          : 'bg-transparent'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <button
            className="p-2 text-gray-500 hover:text-gray-800 focus:outline-none"
            onClick={scrollRight}
          >
            <RightIcon />
          </button>
        </div>
      </div>

      {/* Delete Row Popup */}

      {/* Table Section */}
      <div className="mt-6">
        {activeTabData && !question && (
          <div className="text-center bg-yellow-100 p-5 font-bold text-xs rounded">
            For the {activeTabData.name} School Year{' '}
          </div>
        )}

        {/* Display information notes if present */}
        {activeTabData?.field_responses?.map((field) =>
          field.field_type === 'information' ? (
            <div
              className="mt-4 bg-ds-orange-100 rounded text-ds-orange-900 text-xs"
              key={field.field_id}
            >
              <CustomInformationField
                label={field.label}
                description={field.additional_config?.helpText}
                beforeLinkDescription={field.additional_config?.beforeLinkDescription}
                inlineTriggerText={field.additional_config?.inlineTriggerText}
                modalContent={field.additional_config?.modalContent}
                table_response={[]}
              />
            </div>
          ) : null
        )}

        {activeTabTables.length > 0 ? (
          activeTabTables.map((table, index) => (
            <div key={table.table_id || index}>{renderTable(table, index)}</div>
          ))
        ) : (
          <div className="mt-4 text-gray-500 text-center">No data available for this tab.</div>
        )}

        {/* Only show the attention message if there are incomplete date ranges */}
        {question &&
          !checkDateRangesComplete[activeTab] &&
          activeTabData?.name?.trim()?.toLowerCase() === 'superintendent' && (
            <div className="mt-4 bg-ds-red-100 p-5 rounded text-red-500 text-xs">
              <p>
                <b> Attention: </b>Please ensure that data is provided for the full period from
                September 2017 through March 2025. If any months are missing, kindly complete the
                data before proceeding.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

function isDateLessThan(date1: string, date2: string): boolean {
  if (!date1 || !date2) return false;
  const [m1, y1] = date1.split('/').map(Number);
  const [m2, y2] = date2.split('/').map(Number);
  if (y1 < y2) return true;
  if (y1 === y2 && m1 < m2) return true;
  return false;
}

export default CustomTabsWithTable;
